import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { ProductService } from '../../services/product.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { ColumnPreferenceService } from '../../services/column-preference-service.service';
import { ExportExcelService } from '../../services/export-excel.service';
import { Product, NewProduct, ProductSearchParams } from '../../domain/product.model';
import { ItemMovement } from '../../domain/inventory.model';
import { Account, PageableAccount } from '../../domain/account.model';
import { AccountDropdown } from '../../domain/accountDropdown.model';
import { ColumnConfig } from '../../domain/column_config.model';

@Component({
    selector: 'app-product-catalog',
    templateUrl: './product-catalog.component.html',
    styleUrl: './product-catalog.component.scss',
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class ProductCatalogComponent implements OnInit {
  products: Product[] = [];
  accounts: any[] = [];
  userAccount: Account = {};
  totalRecords: number = 0;
  loading: boolean = true;
  accountLoading: boolean = false;
  exportLoading: boolean = false;

  // Fixed UOM (Medida Base) catalog used across all accounts
  uomOptions: string[] = ['Unidad', 'Caja', 'Tarima'];

  //Account dropdown (only filter shown above the grid)
  filterForm: FormGroup = this.fb.group({
    accountId: [null],
  });
  lastQuery: string = '';
  searchAccountSubject = new Subject<string>();
  searchAccountQuery: string = '';

  tableRows: number = 30;
  firstPage = 0;
  lastLazyEvent: any = null;

  //Column-level filters live in the table header (see template); this keeps the last
  //lazy-load event around so export and account-change reloads can reuse the same filters.

  //New product / variant / edit dialog
  productDialogVisible = false;
  dialogMode: 'create' | 'variant' | 'edit' = 'create';
  parentProduct: Product | null = null;
  editingProduct: Product | null = null;
  productForm: FormGroup = this.fb.group({
    sku: [''],
    variant: [''],
    description: ['', Validators.required],
    upc: [''],
    uom: ['', Validators.required],
    category: ['', Validators.required],
  });

  // Category autocomplete: search existing categories for the selected account, or let
  // the user type a brand-new one (there's no fixed catalog of categories, unlike UOM).
  categorySuggestions: string[] = [];

  // Category column filter: same account-scoped category search, kept separate from the
  // dialog's suggestions so opening the filter panel doesn't clobber the create/edit form.
  categoryFilterSuggestions: string[] = [];

  // History dialog
  historyDialogVisible = false;
  movements: ItemMovement[] = [];
  selectedProduct: Product | null = null;

  //Column toggle
  ALL_COLUMNS: ColumnConfig[] = [
    { field: 'rowIndex', header: '#', type: 'number', alwaysVisible: true },
    { field: 'productId', header: 'ID', type: 'number', sortable: true },
    { field: 'sku', header: 'SKU', type: 'string', sortable: true },    
    { field: 'description', header: 'Descripción', type: 'string', sortable: true },
    { field: 'variant', header: 'Variante', type: 'string', sortable: true },
    { field: 'upc', header: 'UPC', type: 'string', sortable: true },
    { field: 'uom', header: 'UOM (Medida Base)', type: 'string', sortable: true },
    { field: 'category', header: 'Categoría', type: 'string', sortable: true },
    { field: 'receivedQuantity', header: 'Recibidos', type: 'number', sortable: true },
    { field: 'registeredQuantity', header: 'En Inventario', type: 'number', sortable: true },
    { field: 'stockQuantity', header: 'En Stock', type: 'number', sortable: true },
    { field: 'creationDate', header: 'Creado', type: 'date', sortable: true },
    { field: 'updateDate', header: 'Actualizado', type: 'date', sortable: true },
    { field: 'createdBy', header: 'Creado por', type: 'string', sortable: true },
    { field: 'actions', header: 'Acciones', type: 'actions', alwaysVisible: true },
  ];
  columnsToggleList: ColumnConfig[] = [];
  appliedTableColumns: ColumnConfig[] = [];
  columnSearchTerm: string = '';
  selectAll: boolean = false;

  currentRole: string;
  currentUserId: string;

  constructor(
    private productService: ProductService,
    private fb: FormBuilder,
    private accountService: AccountService,
    private authService: AuthService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private colPrefService: ColumnPreferenceService,
    private exportService: ExportExcelService,
  ) {
    this.currentRole = this.authService.getUserRole();
    this.currentUserId = this.authService.getUserDetails()?.userId;
  }

  ngOnInit(): void {
    const savedRows = localStorage.getItem('rowsPerPage');
    if (savedRows) {
      this.tableRows = +savedRows;
    }
    this.initColumns();
    this.fetchInitialData();
    this.searchAccountSubject.pipe(debounceTime(800)).subscribe(searchText => {
      this.searchAccountQuery = searchText;
      this.getAccounts();
    });
  }

  get hasAccountAccess(): boolean {
    return this.authService.canAccessAction('productCatalog', 'accountSelection');
  }

  get hasEditAccess(): boolean {
    return this.authService.canAccessAction('productCatalog', 'edit');
  }

  fetchInitialData() {
    if (this.hasAccountAccess) {
      this.getAccounts(() => this.restoreLastAccount());
    } else {
      this.getAccount();
    }
  }

  loadAccounts(event: any) {
    this.searchAccountSubject.next(event.filter || '');
  }

  getAccounts(onComplete?: () => void) {
    this.accountLoading = true;
    this.accountService.searchAllAccounts(this.searchAccountQuery).subscribe((response: PageableAccount) => {
      this.accounts = response.content;
      this.accountLoading = false;
      onComplete?.();
    });
  }

  getAccount() {
    this.filterForm.get('accountId')?.disable();
    this.accountLoading = true;
    this.userAccount = this.authService.getUserAccount();
    this.filterForm.patchValue({ accountId: this.userAccount.accountId });
    this.accountService.getDropdownAccount(this.userAccount.accountId).subscribe((response: AccountDropdown[]) => {
      this.accounts = response;
      this.accountLoading = false;
      this.reload();
    });
  }

  // Remember the last account this user picked (per user, like rowsPerPage / column prefs
  // elsewhere) so returning users don't land on an empty grid every time — first-time users
  // still see the "select an account" empty state, satisfying the AC that data only shows
  // once an account is explicitly chosen.
  private get lastAccountStorageKey(): string {
    return `catalog_last_account_${this.currentUserId}`;
  }

  private restoreLastAccount() {
    const raw = localStorage.getItem(this.lastAccountStorageKey);
    const lastAccountId = raw ? Number(raw) : null;
    if (!lastAccountId) {
      return;
    }

    this.accountService.getDropdownAccount(lastAccountId).subscribe((response: AccountDropdown[]) => {
      const match = response?.[0];
      if (!match) {
        return;
      }
      // Make sure the p-select can render the label even if this account wasn't part of
      // the default (unfiltered) accounts page already loaded by getAccounts().
      if (!this.accounts.some((a: any) => a.accountId === match.accountId)) {
        this.accounts = [match, ...this.accounts];
      }
      this.filterForm.patchValue({ accountId: match.accountId });
      this.reload();
    });
  }

  onAccountChange() {
    const accountId = this.filterForm.get('accountId')?.value;
    if (accountId) {
      localStorage.setItem(this.lastAccountStorageKey, accountId.toString());
    } else {
      localStorage.removeItem(this.lastAccountStorageKey);
    }
    this.firstPage = 0;
    this.reload();
  }

  reload() {
    this.loadProducts({ ...(this.lastLazyEvent ?? {}), first: 0, rows: this.tableRows });
  }

  private extractConstraints(filters: any, field: string): { value: any; matchMode?: string }[] {
    const meta = filters?.[field];
    if (!meta) return [];
    const list = Array.isArray(meta) ? meta : (meta.constraints ?? [meta]);
    return list.filter((c: any) => c?.value !== null && c?.value !== undefined && c?.value !== '');
  }

  private extractTextFilter(filters: any, field: string): string | undefined {
    const constraints = this.extractConstraints(filters, field);
    return constraints.length ? constraints[0].value : undefined;
  }

  // Numeric columns with operator support (equals/notEquals/lt/lte/gt/gte): the value and
  // the chosen PrimeNG matchMode both travel to the backend, which applies the comparison.
  private extractNumberFilterWithMode(filters: any, field: string): { value?: number; matchMode?: string } {
    const constraints = this.extractConstraints(filters, field);
    if (!constraints.length) return {};
    return { value: Number(constraints[0].value), matchMode: constraints[0].matchMode };
  }

  private extractDateRange(filters: any, field: string): { from?: string; to?: string } {
    const constraints = this.extractConstraints(filters, field);
    let from: Date | undefined;
    let to: Date | undefined;
    constraints.forEach(c => {
      if (c.matchMode === 'dateAfter' || c.matchMode === 'dateIs') from = c.value;
      if (c.matchMode === 'dateBefore' || c.matchMode === 'dateIs') to = c.value;
    });
    return {
      from: from ? from.toISOString() : undefined,
      to: to ? to.toISOString() : undefined,
    };
  }

  private buildSearchParams(event: any, overrides: Partial<ProductSearchParams> = {}): ProductSearchParams {
    const filters = event?.filters ?? {};
    const creationDateRange = this.extractDateRange(filters, 'creationDate');
    const updateDateRange = this.extractDateRange(filters, 'updateDate');
    const productIdFilter = this.extractNumberFilterWithMode(filters, 'productId');
    const stockQuantityFilter = this.extractNumberFilterWithMode(filters, 'stockQuantity');
    const sortField = event?.sortField ?? 'productId';
    const sortOrder = event?.sortOrder === 1 ? 'ASC' : 'DESC';

    return {
      accountId: this.filterForm.get('accountId')?.value ?? undefined,
      productId: productIdFilter.value,
      productIdMatchMode: productIdFilter.matchMode,
      sku: this.extractTextFilter(filters, 'sku'),
      variant: this.extractTextFilter(filters, 'variant'),
      description: this.extractTextFilter(filters, 'description'),
      upc: this.extractTextFilter(filters, 'upc'),
      uom: this.extractTextFilter(filters, 'uom'),
      category: this.extractTextFilter(filters, 'category'),
      createdBy: this.extractTextFilter(filters, 'createdBy'),
      stockQuantity: stockQuantityFilter.value,
      stockQuantityMatchMode: stockQuantityFilter.matchMode,
      creationDateFrom: creationDateRange.from,
      creationDateTo: creationDateRange.to,
      updateDateFrom: updateDateRange.from,
      updateDateTo: updateDateRange.to,
      page: event?.first != null && event?.rows ? event.first / event.rows : 0,
      size: event?.rows ?? this.tableRows,
      sortBy: sortField,
      sortDirection: sortOrder,
      ...overrides,
    };
  }

  loadProducts(event: any) {
    this.loading = true;
    this.lastLazyEvent = event;
    const params = this.buildSearchParams(event);

    if (!params.accountId) {
      this.products = [];
      this.totalRecords = 0;
      this.loading = false;
      return;
    }

    this.productService.searchProducts(params).subscribe({
      next: (data) => {
        this.products = data.content;
        this.totalRecords = data.totalElements;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  exportExcel() {
    const params = this.buildSearchParams(this.lastLazyEvent, { page: 0, size: this.totalRecords || this.tableRows });
    if (!params.accountId || !this.totalRecords) {
      return;
    }

    this.exportLoading = true;
    this.productService.searchProducts(params).subscribe({
      next: (data) => {
        this.exportService.export(data.content, this.appliedTableColumns, 'catalogo_productos');
        this.exportLoading = false;
      },
      error: (error) => {
        this.exportLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo exportar el catálogo' });
      }
    });
  }

  //--- New product / variant / edit dialog ---

  openCreateDialog() {
    this.dialogMode = 'create';
    this.parentProduct = null;
    this.editingProduct = null;
    this.productForm.reset();
    this.productDialogVisible = true;
  }

  openVariantDialog(product: Product) {
    this.dialogMode = 'variant';
    this.parentProduct = product;
    this.editingProduct = null;
    this.prefillForm(product);
    this.productDialogVisible = true;
  }

  openEditDialog(product: Product) {
    this.dialogMode = 'edit';
    this.parentProduct = null;
    this.editingProduct = product;
    this.prefillForm(product);
    this.productDialogVisible = true;
  }

  private prefillForm(product: Product) {
    this.productForm.reset();
    this.productForm.patchValue({
      sku: product.sku,
      variant: product.variant,
      description: product.description,
      upc: product.upc,
      uom: product.uom,
      category: product.category,
    });
  }

  searchCategories(event: AutoCompleteCompleteEvent) {
    const accountId = this.filterForm.get('accountId')?.value;
    if (!accountId) {
      this.categorySuggestions = [];
      return;
    }
    this.productService.getCategories(accountId, event.query).subscribe(categories => {
      this.categorySuggestions = categories;
    });
  }

  searchFilterCategories(event: AutoCompleteCompleteEvent) {
    const accountId = this.filterForm.get('accountId')?.value;
    if (!accountId) {
      this.categoryFilterSuggestions = [];
      return;
    }
    this.productService.getCategories(accountId, event.query).subscribe(categories => {
      // With no query (dropdown opened without typing) cap the list to the last 10
      // categories instead of dumping the account's full catalog into the panel.
      this.categoryFilterSuggestions = event.query ? categories : categories.slice(-10);
    });
  }

  // Trims and treats null/undefined/'' as equivalent, so whitespace-only edits (e.g. typing
  // a space then deleting it) can't be mistaken for a real change and let a duplicate through.
  private normalize(value: any): string {
    return (value ?? '').toString().trim();
  }

  get isVariantUnchanged(): boolean {
    if (this.dialogMode !== 'variant' || !this.parentProduct) {
      return false;
    }
    const value = this.productForm.value;
    return this.normalize(value.sku) === this.normalize(this.parentProduct.sku) &&
      this.normalize(value.variant) === this.normalize(this.parentProduct.variant) &&
      this.normalize(value.description) === this.normalize(this.parentProduct.description) &&
      this.normalize(value.upc) === this.normalize(this.parentProduct.upc) &&
      this.normalize(value.uom) === this.normalize(this.parentProduct.uom) &&
      this.normalize(value.category) === this.normalize(this.parentProduct.category);
  }

  get dialogTitle(): string {
    if (this.dialogMode === 'variant') return `Crear Variante de ${this.parentProduct?.sku ?? ''}`;
    if (this.dialogMode === 'edit') return 'Editar Producto';
    return 'Nuevo Producto';
  }

  save() {
    if (this.productForm.invalid || this.isVariantUnchanged) {
      this.productForm.markAllAsTouched();
      return;
    }

    const accountId = this.filterForm.get('accountId')?.value;
    if (!accountId) {
      return;
    }

    const raw = this.productForm.getRawValue();
    const payload: NewProduct = {
      ...raw,
      sku: this.normalize(raw.sku),
      variant: this.normalize(raw.variant),
      description: this.normalize(raw.description),
      upc: this.normalize(raw.upc),
      category: this.normalize(raw.category),
      accountId,
    };

    const request$ = this.dialogMode === 'variant' && this.parentProduct
      ? this.productService.createVariant(this.parentProduct.productId, payload)
      : this.dialogMode === 'edit' && this.editingProduct
        ? this.productService.updateProduct(this.editingProduct.productId, payload)
        : this.productService.createProduct(payload);

    request$.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Exitoso', detail: 'Producto guardado correctamente', life: 5000 });
        this.reload();

        if (this.dialogMode === 'edit') {
          this.productDialogVisible = false;
          return;
        }

        this.confirmationService.confirm({
          header: 'Producto guardado',
          message: '¿Deseas agregar otro producto?',
          icon: 'pi pi-question-circle',
          acceptLabel: 'Sí',
          rejectLabel: 'No',
          accept: () => this.prefillOrResetForContinue(),
          reject: () => {
            this.productDialogVisible = false;
          }
        });
      },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo guardar el producto' });
      }
    });
  }

  private prefillOrResetForContinue() {
    if (this.dialogMode === 'variant' && this.parentProduct) {
      this.prefillForm(this.parentProduct);
    } else {
      this.productForm.reset();
    }
  }

  //--- History dialog ---

  showHistory(product: Product) {
    this.selectedProduct = product;
    this.productService.getProductMovements(product.productId).subscribe(data => {
      this.movements = data.map(m => ({
        ...m,
        changeDetail: typeof m.changeDetail === 'string' ? JSON.parse(m.changeDetail) : m.changeDetail
      }));
      if (data.length > 0) this.historyDialogVisible = true;
    });
  }

  getFieldLabel(key: string): string {
    const labels: Record<string, string> = {
      sku: 'SKU',
      variant: 'Variante',
      description: 'Descripción',
      upc: 'UPC',
      uom: 'UOM',
      category: 'Categoría'
    };
    return labels[key] || key;
  }

  getFieldValue(value: any): string {
    return value ?? 'null';
  }

  //--- Column visibility toggle ---

  initColumns() {
    const allFields = this.ALL_COLUMNS.map(c => c.field);
    const saved = this.colPrefService.load(this.currentUserId, 'ProductCatalog');

    this.columnsToggleList = [...this.ALL_COLUMNS];

    const activeFields = saved
      ? this.colPrefService.reconcile(saved, allFields)
      : allFields.filter(f => {
        const col = this.ALL_COLUMNS.find(c => c.field === f);
        return col && !col.defaultHidden;
      });

    this.columnsToggleList.forEach(c => {
      c.visible = c.alwaysVisible ? true : activeFields.includes(c.field);
    });

    this.applyColumns(activeFields);
  }

  applyColumns(fields: string[]) {
    const alwaysVisible = this.columnsToggleList.filter(c => c.alwaysVisible).map(c => c.field);
    const merged = [...new Set([...alwaysVisible, ...fields])];
    this.appliedTableColumns = this.columnsToggleList.filter(c => merged.includes(c.field));
  }

  onSaveColumns() {
    const selected = this.columnsToggleList.filter(c => c.visible || c.alwaysVisible).map(c => c.field);
    this.colPrefService.save(this.currentUserId, 'ProductCatalog', selected);
    this.applyColumns(selected);
  }

  onClearColumns() {
    this.columnsToggleList.forEach(c => c.visible = c.alwaysVisible ?? false);
    this.selectAll = false;
    this.applyColumns([]);
  }

  get filteredColumns(): ColumnConfig[] {
    if (!this.columnSearchTerm.trim()) return this.columnsToggleList;
    return this.columnsToggleList.filter(c =>
      c.header.toLowerCase().includes(this.columnSearchTerm.toLowerCase())
    );
  }

  onSelectAll(event: any) {
    this.columnsToggleList.forEach(c => {
      if (!c.alwaysVisible) c.visible = event.checked;
    });
  }

  onColumnToggle() {
    const toggleable = this.columnsToggleList.filter(c => !c.alwaysVisible);
    this.selectAll = toggleable.every(c => c.visible);
  }

  resolveField(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }
}
